import StyledErrorMessage from '@/src/common/components/elements/Alerts/StyledErrorMessage'
import {
    PopoverContent,
    PopoverRoot,
    PopoverTrigger,
} from '@/src/common/components/elements/Buttons/Popover'
import { CheckboxInputWithLabel } from '@/src/common/components/elements/Forms/CheckboxInputWithLabel'
import EmailInput from '@/src/common/components/elements/Inputs/EmailInput'
import NameInput from '@/src/common/components/elements/Inputs/NameInput'
import PhoneNumberInput from '@/src/common/components/elements/Inputs/PhoneNumberInput'
import Button from '@/src/common/components/marketing/buttons/Button'
import usePostKYCApplication from '@/src/common/hooks/usePostKYCApplication'
import { IKYCApplicationInfo } from '@/src/common/queries/PostKYCApplication'
import handleEnterPressEvent from '@/src/common/utilities/handleEnterPressEvent'
import { addressMax, emailMax } from '@/src/modules/build-a-card/constants'
import DefaultAddressForm from '@/src/modules/checkout/forms/DefaultAddressForm'
import { StyledForm } from '@/src/modules/checkout/forms/StyledComponents'
import { addressValidationSchema } from '@/src/modules/checkout/schemas'
import { PHONE_REG_EXP } from '@/src/modules/pg-plus/constants'
import { IFullWidthFields } from '@/src/modules/smarty/utilities'
import { styled } from '@/src/stitches.config'
import { faQuestion } from '@fortawesome/free-solid-svg-icons/faQuestion'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useEffect, useState } from 'react'
import * as yup from 'yup'
import { ValidationError } from 'yup'
import { FieldWrapperWithGap } from '@/src/common/components/elements/Forms/StyledComponents'

const StyledCheckbox = styled(CheckboxInputWithLabel, {
    my: 15,
})

const KYCApplicationSchema = addressValidationSchema.shape({
    email: yup
        .string()
        .trim()
        .label('Email address')
        .required()
        .max(emailMax, `Email cannot contain more than ${emailMax} characters`)
        .email(),
    phone: yup
        .string()
        .required()
        .label('Phone number')
        .matches(PHONE_REG_EXP, 'A valid phone number is required'),
    companyName: yup.string().required().label('Company name'),
    companyWebsite: yup.string().optional().label('Company website'),
    reason: yup.string().required().label('Reason for interest'),
    ein: yup.string().required().label('EIN').min(9),
    userConfirmedInfo: yup.boolean().oneOf([true]),
    agreedToTerms: yup.boolean().oneOf([true]),
})

function getCheckoutErrorMessage(formErrors: ValidationError[]) {
    const hasAgreementError: boolean = !!formErrors?.find(
        (e) => e.path === 'agreedToTerms'
    )
    const hasConfirmationError: boolean = !!formErrors?.find(
        (e) => e.path === 'userConfirmedInfo'
    )

    if (!hasAgreementError && !hasConfirmationError) {
        return null
    }

    let error: string = `We're sorry. Your order cannot continue to checkout unless you agree ${
        hasConfirmationError ? 'that all information provided is accurate' : ''
    }${hasAgreementError && hasConfirmationError ? ' and ' : ''}${
        hasAgreementError
            ? 'that the products you are purchasing will not be used for resale'
            : ''
    }.`

    return error
}

const KYCApplicationForm = ({
    setHasKycApplication,
}: {
    setHasKycApplication: Function
}) => {
    const {
        mutate: postApplication,
        isLoading: applicationIsLoading,
        isSuccess: applicationIsSuccess,
    } = usePostKYCApplication()

    const [name, setName] = useState<string>('')
    const [email, setEmail] = useState<string>('')
    const [phone, setPhone] = useState<string>('')
    const [companyName, setCompanyName] = useState<string>('')
    const [reason, setReason] = useState<string>('')
    const [companyWebsite, setCompanyWebsite] = useState<string>('')
    const [ein, setEin] = useState<string>('')
    const [address1, setAddress1] = useState<string>('')
    const [address2, setAddress2] = useState<string>('')
    const [city, setCity] = useState<string>('')
    const [state, setState] = useState<string>('')
    const [zip, setZip] = useState<string>('')

    const [userConfirmedInfo, setUserConfirmedInfo] = useState<boolean>(false)
    const [agreedToTerms, setAgreedToTerms] = useState<boolean>(false)
    const [formErrors, setFormErrors] = useState<ValidationError[]>([])
    const [isLoading, setIsLoading] = useState(false)
    const [displayLiveErrors, setDisplayLiveErrors] = useState<boolean>(false)
    const checkboxError: string | null = getCheckoutErrorMessage(formErrors)
    const checkboxCSS: object = { fontSize: '.9rem', lineHeight: '1.25rem' }
    const [postErrorText, setPostErrorText] = useState<string | null>(null)

    useEffect(() => {
        if (!displayLiveErrors) {
            return
        }

        KYCApplicationSchema.validate(
            {
                name,
                phone,
                email,
                companyName,
                ein,
                companyWebsite,
                reason,
                address1,
                address2,
                city,
                state,
                zip,
                agreedToTerms,
                userConfirmedInfo,
            },
            { abortEarly: false }
        )
            .then(() => {
                setFormErrors([])
            })
            .catch((err) => {
                if (displayLiveErrors) {
                    setFormErrors(err.inner)
                }
            })
    }, [
        name,
        phone,
        email,
        companyName,
        ein,
        companyWebsite,
        reason,
        address1,
        address2,
        city,
        state,
        zip,
        agreedToTerms,
        userConfirmedInfo,
        displayLiveErrors,
    ])

    const AdditionalFullWidthFields: IFullWidthFields[] = [
        {
            id: 'companyName',
            value: companyName,
            placeholder: 'Company name',
            label: 'Company name',
            setField: setCompanyName,
            autoComplete: 'organization',
        },
        {
            id: 'reason',
            value: reason,
            placeholder: 'Reason for interest',
            label: 'Reason for interest',
            setField: setReason,
            autoComplete: 'false',
        },
        {
            id: 'companyWebsite',
            value: companyWebsite,
            placeholder: 'Company website',
            label: 'Company website',
            setField: setCompanyWebsite,
            autoComplete: 'false',
        },
        {
            id: 'ein',
            value: ein,
            placeholder: 'EIN',
            label: 'EIN',
            setField: setEin,
            format: '##-#######',
            autoComplete: 'false',
            className: 'fs-mask fs-exclude',
        },
        {
            id: 'address1',
            value: address1,
            placeholder: 'Address Line 1',
            label: 'Address Line 1',
            setField: setAddress1,
            maxLength: addressMax,
            autoComplete: 'address-line1',
        },
        {
            id: 'address2',
            value: address2,
            placeholder: 'Address Line 2',
            label: 'Address Line 2',
            setField: setAddress2,
            maxLength: addressMax,
            autoComplete: 'address-line2',
        },
        {
            id: 'city',
            value: city,
            placeholder: 'City',
            label: 'City',
            setField: setCity,
            autoComplete: 'address-level2',
        },
    ]

    return (
        <StyledForm style={{ paddingBottom: 40 }}>
            <>
                <p className={'form-header'}>Contact information</p>
                <FieldWrapperWithGap>
                    <NameInput
                        name={name}
                        setName={setName}
                        formErrors={formErrors}
                        setFormErrors={setFormErrors}
                        fieldId={'name'}
                    />
                    <EmailInput
                        email={email}
                        setEmail={setEmail}
                        formErrors={formErrors}
                        setFormErrors={setFormErrors}
                        fieldId={'email'}
                    />
                    <PhoneNumberInput
                        phone={phone}
                        setPhone={setPhone}
                        formErrors={formErrors}
                        setFormErrors={setFormErrors}
                        fieldId={'phone'}
                    />
                </FieldWrapperWithGap>
                <h2 className={'form-header'} style={{ marginBottom: 0 }}>
                    Additional information
                </h2>
                <p
                    style={{
                        marginBottom: 30,
                        lineHeight: '1.5rem',
                        fontSize: '.85rem',
                        display: 'flex',
                    }}
                >
                    Why do we need this information and how will it be used?{' '}
                    <span style={{ marginLeft: 8 }}>
                        <PopoverRoot>
                            <PopoverTrigger variant={'purple'}>
                                <FontAwesomeIcon icon={faQuestion} />
                            </PopoverTrigger>
                            <PopoverContent>
                                Due to the nature of gift card products, our
                                PerfectGift.com compliance team needs this
                                additional information to verify your account
                                and transaction details, which permits you to
                                order more than our standard limits. This
                                information is for verification purposes only,
                                and is used in accordance to our{' '}
                                <a
                                    rel="noreferrer"
                                    target="_blank"
                                    href={'/privacy-policy'}
                                >
                                    Privacy Policy
                                </a>
                            </PopoverContent>
                        </PopoverRoot>
                    </span>
                </p>
                <DefaultAddressForm
                    fullWidthFields={AdditionalFullWidthFields}
                    state={state}
                    zip={zip}
                    formErrors={formErrors}
                    setState={setState}
                    setZip={setZip}
                    setFormErrors={setFormErrors}
                />
                <StyledCheckbox
                    label={
                        'By checking this box, I agree that all of the information provided is accurate.'
                    }
                    value={userConfirmedInfo}
                    name={'userConfirmedInfo'}
                    data-testid={'user-confirmed-info'}
                    onCheckedChange={() => {
                        setUserConfirmedInfo(!userConfirmedInfo)
                    }}
                    onKeyDown={(e: any) => {
                        handleEnterPressEvent(e, () =>
                            setUserConfirmedInfo(!userConfirmedInfo)
                        )
                    }}
                    checked={userConfirmedInfo}
                    labelCss={checkboxCSS}
                />
                <StyledCheckbox
                    label={
                        'I agree to not use the purchased products for resale purposes.'
                    }
                    value={agreedToTerms}
                    name={'agreedToTerms'}
                    data-testid={'agreed-to-terms'}
                    onCheckedChange={() => {
                        setAgreedToTerms(!agreedToTerms)
                    }}
                    onKeyDown={(e: any) => {
                        handleEnterPressEvent(e, () =>
                            setAgreedToTerms(!agreedToTerms)
                        )
                    }}
                    checked={agreedToTerms}
                    labelCss={checkboxCSS}
                />
                {(checkboxError || postErrorText) && (
                    <StyledErrorMessage
                        errorText={checkboxError ?? postErrorText ?? ''}
                        style={{ marginTop: 10 }}
                    />
                )}
                <Button
                    id={'submit-button'}
                    data-testid={'submit-button'}
                    label={'Continue to payment'}
                    width={'standard'}
                    textTransform={'uppercase'}
                    showArrow={true}
                    variant={'primary'}
                    isLoading={isLoading || applicationIsLoading}
                    disabled={
                        isLoading ||
                        applicationIsLoading ||
                        applicationIsSuccess
                    }
                    css={{ marginTop: 20, fontSize: '1.15rem' }}
                    type={'button'}
                    onClick={async () => {
                        setIsLoading(true)
                        setPostErrorText(null)
                        KYCApplicationSchema.validate(
                            {
                                name,
                                phone,
                                email,
                                companyName,
                                ein,
                                companyWebsite,
                                reason,
                                address1,
                                address2,
                                city,
                                state,
                                zip,
                                agreedToTerms,
                                userConfirmedInfo,
                            },
                            {
                                abortEarly: false,
                                context: { checkLanguage: true },
                            }
                        )
                            .then(async () => {
                                const applicationPayload: IKYCApplicationInfo =
                                    {
                                        contact_name: name,
                                        company_name: companyName,
                                        contact_phone: phone,
                                        email: email,
                                        ein_ssn: ein,
                                        website: companyWebsite,
                                        address_line1: address1,
                                        address_line2: address2,
                                        city: city,
                                        state: state,
                                        zip: zip,
                                        reason_for_interest: reason,
                                    }

                                postApplication(applicationPayload, {
                                    onSuccess: () => {
                                        setHasKycApplication(true)
                                    },
                                    onError: () => {
                                        setPostErrorText(
                                            'Error: Unable to submit form. Please verify all form fields and try again.'
                                        )
                                        setIsLoading(false)
                                    },
                                })
                            })
                            .catch((err) => {
                                setIsLoading(false)
                                console.error(err)
                                setFormErrors(err.inner)
                                setDisplayLiveErrors(true)
                            })
                    }}
                />
            </>
        </StyledForm>
    )
}

export default KYCApplicationForm
